import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "../lib/trpc.js";

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return "now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Query for notifications with 30-second polling
  const { data } = trpc.notifications.list.useQuery(undefined, {
    refetchInterval: 30000,
  });

  // Mutation to mark all as read
  const markAllReadMutation = trpc.notifications.markAllRead.useMutation();

  // Handle opening the dropdown
  const handleOpenDropdown = () => {
    setIsOpen(true);
    // Optimistically mark all as read
    markAllReadMutation.mutate();
  };

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Bell button */}
      <button
        onClick={handleOpenDropdown}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
          height: "24px",
        }}
        aria-label="Notifications"
      >
        {/* Bell SVG */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: "var(--color-foreground)" }}
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {/* Dot indicator */}
        {data?.hasUnread && (
          <div
            style={{
              position: "absolute",
              top: "-2px",
              right: "-2px",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "var(--color-accent)",
            }}
          />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: "8px",
            width: "min(320px, calc(100vw - 32px))",
            maxHeight: "400px",
            backgroundColor: "var(--color-card)",
            border: `1px solid var(--color-border)`,
            borderRadius: "12px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "12px 16px",
              borderBottom: `1px solid var(--color-border)`,
              fontSize: "14px",
              fontWeight: "500",
              color: "var(--color-foreground)",
            }}
          >
            {t("notifications.title")}
          </div>

          {/* Notification list */}
          <div
            style={{
              overflowY: "auto",
              flex: 1,
            }}
          >
            {!data?.items || data.items.length === 0 ? (
              <div
                style={{
                  padding: "24px 16px",
                  textAlign: "center",
                  color: "var(--color-muted-foreground)",
                  fontSize: "13px",
                }}
              >
                {t("notifications.empty")}
              </div>
            ) : (
              <div>
                {data.items.map((notification) => (
                  <div
                    key={notification.id}
                    style={{
                      padding: "12px 16px",
                      borderBottom: `1px solid var(--color-border)`,
                      fontSize: "13px",
                      color: "var(--color-foreground)",
                      cursor: "default",
                    }}
                  >
                    <div style={{ marginBottom: "4px" }}>{notification.body}</div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "var(--color-muted-foreground)",
                      }}
                    >
                      {formatRelativeTime(new Date(notification.createdAt))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
